import { Finding, ValidationResult, DISCLAIMER } from './types';

const RULEPACK_VERSION = 'aasb116-1.0.0';

type CellValue = string | number | boolean | null;

interface AssetRow {
  asset_id: string;
  asset_class: string;
  purchase_date: Date;
  cost: number;
  useful_life_years: number;
  depreciation_method: 'straight_line' | 'diminishing_value' | 'units_of_production' | 'unknown';
  residual_value: number | null;
  accumulated_depreciation: number;
  carrying_value: number;
  report_date: Date;
  revaluation_model?: boolean;
}

// ---- header mapping ----

const HEADER_MAP: Record<string, string> = {};
function reg(canonical: string, ...synonyms: string[]) {
  HEADER_MAP[canonical.toLowerCase()] = canonical;
  for (const s of synonyms) HEADER_MAP[s.toLowerCase()] = canonical;
}
reg('asset_id', 'asset id', 'asset no', 'asset number', 'id');
reg('asset_class', 'asset class', 'class', 'category');
reg('purchase_date', 'purchase date', 'acquisition date', 'date acquired', 'in-service date', 'in service date');
reg('cost', 'original cost', 'acquisition cost', 'historical cost');
reg('useful_life_years', 'useful life', 'useful life (years)', 'life', 'depreciable life');
reg('depreciation_method', 'depreciation method', 'method', 'dep method');
reg('residual_value', 'residual value', 'salvage value', 'residual');
reg('accumulated_depreciation', 'accumulated depreciation', 'accum depn', 'acc depreciation', 'accumulated depn');
reg('carrying_value', 'carrying value', 'net book value', 'nbv', 'written down value', 'wdv');
reg('report_date', 'report date', 'reporting date', 'as at');
reg('revaluation_model', 'revaluation model', 'revalued');

const REQUIRED: (keyof AssetRow)[] = [
  'asset_id', 'asset_class', 'purchase_date', 'cost',
  'useful_life_years', 'depreciation_method',
  'accumulated_depreciation', 'carrying_value',
];

function normaliseHeader(h: string): string | null {
  const key = String(h ?? '').trim().toLowerCase();
  return HEADER_MAP[key] ?? null;
}

function normaliseMethod(v: unknown): AssetRow['depreciation_method'] {
  const s = String(v ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (s.startsWith('straight') || s === 'sl' || s === 'straight_line') return 'straight_line';
  if (s.startsWith('diminishing') || s.startsWith('reducing') || s === 'dv' || s === 'diminishing_value') return 'diminishing_value';
  if (s.startsWith('units') || s === 'uop' || s === 'units_of_production') return 'units_of_production';
  return 'unknown';
}

function parseNumber(v: unknown): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).replace(/[$,£€ ]/g, '').trim();
  const neg = /^\(.*\)$/.test(s);
  const clean = neg ? s.slice(1, -1) : s;
  const n = Number(clean);
  if (!Number.isFinite(n)) return null;
  return neg ? -n : n;
}

function parseDate(v: unknown): Date | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) return isNaN(+v) ? null : v;
  if (typeof v === 'number') {
    // Excel serial date (days since 1899-12-30, accounting for 1900 leap bug)
    const ms = (v - 25569) * 86400 * 1000;
    const d = new Date(ms);
    return isNaN(+d) ? null : d;
  }
  const s = String(v).trim();
  // DD/MM/YYYY
  const au = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (au) {
    const [, dd, mm, yyyy] = au;
    const year = yyyy.length === 2 ? 2000 + Number(yyyy) : Number(yyyy);
    const d = new Date(year, Number(mm) - 1, Number(dd));
    return isNaN(+d) ? null : d;
  }
  const iso = new Date(s);
  return isNaN(+iso) ? null : iso;
}

function monthsBetween(a: Date, b: Date): number {
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) +
    (b.getDate() >= a.getDate() ? 0 : -1);
}

function ulid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

function mkFinding(
  ruleId: string,
  severity: Finding['severity'],
  title: string,
  message: string,
  opts: Partial<Pick<Finding, 'rowId' | 'evidence' | 'suggestedAdjustment'>> = {}
): Finding {
  return {
    id: ulid(),
    ruleId,
    standard: 'AASB_116',
    severity,
    title,
    message,
    evidence: opts.evidence ?? {},
    suggestedAdjustment: opts.suggestedAdjustment,
    rowId: opts.rowId,
    rulepackVersion: RULEPACK_VERSION,
  };
}

// ---- main ----

export function validateAASB116(
  headerRow: string[],
  dataRows: CellValue[][],
  defaultReportDate: Date = new Date()
): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];

  // 1. Map headers
  const columnIndex: Partial<Record<keyof AssetRow, number>> = {};
  headerRow.forEach((raw, idx) => {
    const canonical = normaliseHeader(String(raw ?? ''));
    if (canonical) (columnIndex as any)[canonical] = idx;
  });

  for (const field of REQUIRED) {
    if (columnIndex[field] === undefined) {
      schemaErrors.push(`Missing required column for "${field}". Accepted headers include: ${acceptedHeadersFor(field).join(', ')}.`);
    }
  }
  if (schemaErrors.length) {
    return {
      summary: {
        standard: 'AASB_116',
        rulepackVersion: RULEPACK_VERSION,
        rowsTested: 0,
        compliant: 0,
        byseverity: { info: 0, warning: 0, error: 0 },
        runAt: new Date().toISOString(),
      },
      findings,
      schemaErrors,
      disclaimer: DISCLAIMER,
    };
  }

  // 2. Normalise rows
  const rows: AssetRow[] = [];
  dataRows.forEach((raw, i) => {
    const rowNum = i + 2; // header is row 1 in user terms
    const get = (k: keyof AssetRow) => raw[columnIndex[k] as number];

    const purchase = parseDate(get('purchase_date'));
    const cost = parseNumber(get('cost'));
    const life = parseNumber(get('useful_life_years'));
    const accum = parseNumber(get('accumulated_depreciation'));
    const carry = parseNumber(get('carrying_value'));
    const residual = parseNumber(get('residual_value'));
    const method = normaliseMethod(get('depreciation_method'));
    const reportDate = columnIndex.report_date !== undefined
      ? (parseDate(get('report_date')) ?? defaultReportDate)
      : defaultReportDate;

    const rowId = String(get('asset_id') ?? `row-${rowNum}`);

    // R01
    const missing: string[] = [];
    for (const f of REQUIRED) {
      const v = get(f);
      if (v == null || v === '') missing.push(f);
    }
    if (missing.length) {
      findings.push(mkFinding('AASB116.R01', 'error',
        'Required field missing',
        `Row ${rowNum} (${rowId}): missing ${missing.join(', ')}.`,
        { rowId, evidence: { missing, rowNum } }
      ));
      return; // can't evaluate further
    }

    // R02, R03, R04
    if (!(cost !== null && cost > 0)) {
      findings.push(mkFinding('AASB116.R02', 'error', 'Cost must be positive',
        `Row ${rowNum} (${rowId}): cost is ${cost}.`, { rowId, evidence: { cost } }));
      return;
    }
    if (!(life !== null && life > 0)) {
      findings.push(mkFinding('AASB116.R03', 'error', 'Useful life must be positive',
        `Row ${rowNum} (${rowId}): useful life is ${life}. Depreciation cannot be calculated.`,
        { rowId, evidence: { useful_life_years: life } }));
      return;
    }
    if (purchase && purchase > reportDate) {
      findings.push(mkFinding('AASB116.R04', 'error', 'Purchase date in the future',
        `Row ${rowNum} (${rowId}): purchase date ${purchase.toISOString().slice(0, 10)} is after report date.`,
        { rowId, evidence: { purchase_date: purchase.toISOString(), report_date: reportDate.toISOString() } }));
      return;
    }

    // R05
    let effectiveResidual = residual ?? 0;
    if (residual === null) {
      findings.push(mkFinding('AASB116.R05', 'warning', 'Residual value missing — assumed $0',
        `Row ${rowNum} (${rowId}): residual value blank; treated as $0 for the calculation.`,
        { rowId, evidence: { cost } }));
    } else if (residual < 0 || residual > cost) {
      findings.push(mkFinding('AASB116.R05', 'warning', 'Residual value out of range',
        `Row ${rowNum} (${rowId}): residual ${residual} outside [0, cost=${cost}].`,
        { rowId, evidence: { residual, cost } }));
      effectiveResidual = Math.max(0, Math.min(cost, residual));
    }

    // R06
    if (method === 'unknown') {
      findings.push(mkFinding('AASB116.R06', 'warning', 'Unknown depreciation method — recalc skipped',
        `Row ${rowNum} (${rowId}): method not recognised; supported values are straight_line, diminishing_value, units_of_production.`,
        { rowId, evidence: { method: get('depreciation_method') } }));
    }

    rows.push({
      asset_id: String(rowId),
      asset_class: String(get('asset_class') ?? ''),
      purchase_date: purchase ?? reportDate,
      cost,
      useful_life_years: life,
      depreciation_method: method,
      residual_value: effectiveResidual,
      accumulated_depreciation: accum!,
      carrying_value: carry!,
      report_date: reportDate,
      revaluation_model: Boolean(get('revaluation_model')),
    });
  });

  // 3. Per-row deep rules
  const classLifeBuckets: Record<string, number[]> = {};
  let totalCarrying = 0;
  let aggregateDelta = 0;

  for (const r of rows) {
    totalCarrying += r.carrying_value;

    const base = r.cost - (r.residual_value ?? 0);
    const tol = Math.max(10, 0.005 * r.cost);
    const monthsElapsedRaw = monthsBetween(r.purchase_date, r.report_date);
    const monthsUseful = r.useful_life_years * 12;
    const m_elapsed = Math.max(0, Math.min(monthsElapsedRaw, monthsUseful));

    // R15 revaluation model
    if (r.revaluation_model) {
      findings.push(mkFinding('AASB116.R15', 'info',
        'Revaluation model flagged — recalc skipped',
        `Asset ${r.asset_id} is on the revaluation model; AASB 116 requires periodic fair-value reassessment (see AASB 13). Automated recalculation does not apply.`,
        { rowId: r.asset_id, evidence: { cost: r.cost, carrying: r.carrying_value } }));
      continue;
    }

    // R07/R08 recalc
    let A_expected: number | null = null;
    if (r.depreciation_method === 'straight_line') {
      const monthly = base / monthsUseful;
      A_expected = Math.min(monthly * m_elapsed, base);
    } else if (r.depreciation_method === 'diminishing_value') {
      const rate = 2 * (1 / r.useful_life_years);
      const yearsElapsed = m_elapsed / 12;
      const carryExpected = Math.max(r.residual_value ?? 0, r.cost * Math.pow(1 - rate, yearsElapsed));
      A_expected = r.cost - carryExpected;
    }
    // R09 units-of-production is not yet calculable with the base schema → R06 already flagged

    if (A_expected !== null) {
      const delta = r.accumulated_depreciation - A_expected;
      aggregateDelta += delta;
      const absDelta = Math.abs(delta);
      if (absDelta > tol) {
        const severity: Finding['severity'] = absDelta > 2 * tol ? 'error' : 'warning';
        findings.push(mkFinding(
          r.depreciation_method === 'straight_line' ? 'AASB116.R07' : 'AASB116.R08',
          severity,
          'Depreciation recalculation differs from reported',
          `Asset ${r.asset_id}: reported accumulated $${fmt(r.accumulated_depreciation)}, expected $${fmt(A_expected)} (delta ${delta >= 0 ? '+' : '−'}$${fmt(Math.abs(delta))}, tolerance ±$${fmt(tol)}).`,
          {
            rowId: r.asset_id,
            evidence: { reported: r.accumulated_depreciation, expected: A_expected, delta, tolerance: tol, method: r.depreciation_method, m_elapsed, monthsUseful },
            suggestedAdjustment: {
              field: 'accumulated_depreciation',
              from: r.accumulated_depreciation,
              to: +A_expected.toFixed(2),
              rationale: `Recalculated per ${r.depreciation_method}, ${m_elapsed}/${monthsUseful} months elapsed, depreciable base $${fmt(base)}.`,
            },
          }
        ));
      }
    }

    // R10 internal consistency
    const V_expected = r.cost - r.accumulated_depreciation;
    const consistencyDelta = Math.abs(r.carrying_value - V_expected);
    if (consistencyDelta > tol) {
      findings.push(mkFinding('AASB116.R10',
        consistencyDelta > 2 * tol ? 'error' : 'warning',
        'Carrying value does not reconcile',
        `Asset ${r.asset_id}: carrying value $${fmt(r.carrying_value)} ≠ cost − accumulated ($${fmt(r.cost)} − $${fmt(r.accumulated_depreciation)} = $${fmt(V_expected)}).`,
        {
          rowId: r.asset_id,
          evidence: { reported: r.carrying_value, expected: V_expected, delta: r.carrying_value - V_expected },
          suggestedAdjustment: {
            field: 'carrying_value', from: r.carrying_value, to: +V_expected.toFixed(2),
            rationale: 'Internal consistency: carrying value = cost − accumulated depreciation.',
          },
        }));
    }

    // R11 negative carrying
    if (r.carrying_value < 0 || V_expected < 0) {
      findings.push(mkFinding('AASB116.R11', 'error', 'Negative carrying value',
        `Asset ${r.asset_id}: carrying value cannot be negative (${r.carrying_value}).`,
        { rowId: r.asset_id, evidence: { carrying: r.carrying_value } }));
    }

    // R12 fully depreciated
    if (m_elapsed >= monthsUseful) {
      if (r.accumulated_depreciation < base - tol) {
        findings.push(mkFinding('AASB116.R12', 'warning', 'Useful life expired but not fully depreciated',
          `Asset ${r.asset_id}: ${m_elapsed} months elapsed vs useful life ${monthsUseful}; accumulated depreciation $${fmt(r.accumulated_depreciation)} < depreciable base $${fmt(base)}.`,
          { rowId: r.asset_id, evidence: { m_elapsed, monthsUseful, base, accumulated: r.accumulated_depreciation } }));
      }
    }

    // R13 over-depreciation
    if (r.accumulated_depreciation > r.cost) {
      findings.push(mkFinding('AASB116.R13', 'error', 'Accumulated depreciation exceeds cost',
        `Asset ${r.asset_id}: accumulated $${fmt(r.accumulated_depreciation)} > cost $${fmt(r.cost)}.`,
        { rowId: r.asset_id, evidence: { accumulated: r.accumulated_depreciation, cost: r.cost } }));
    } else if (r.accumulated_depreciation > base + tol) {
      findings.push(mkFinding('AASB116.R13', 'warning', 'Accumulated depreciation exceeds depreciable base',
        `Asset ${r.asset_id}: accumulated $${fmt(r.accumulated_depreciation)} > depreciable base $${fmt(base)} (residual effectively breached).`,
        { rowId: r.asset_id, evidence: { accumulated: r.accumulated_depreciation, base, residual: r.residual_value } }));
    }

    // R14 class sanity (broad bands)
    const band = CLASS_LIFE_BANDS[r.asset_class.toLowerCase()];
    if (band) {
      if (r.useful_life_years < band[0] || r.useful_life_years > band[1]) {
        findings.push(mkFinding('AASB116.R14', 'info',
          'Useful life outside typical class band',
          `Asset ${r.asset_id} (${r.asset_class}): useful life ${r.useful_life_years}y outside typical band ${band[0]}–${band[1]}y. Confirm or override tenant band.`,
          { rowId: r.asset_id, evidence: { useful_life_years: r.useful_life_years, band } }));
      }
    }

    // accumulate for R16
    (classLifeBuckets[r.asset_class] ??= []).push(r.useful_life_years);
  }

  // R16 dataset outliers
  for (const [cls, lives] of Object.entries(classLifeBuckets)) {
    if (lives.length < 4) continue;
    const sorted = [...lives].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    for (const r of rows.filter(x => x.asset_class === cls)) {
      if (Math.abs(r.useful_life_years - median) > median * 0.5) {
        findings.push(mkFinding('AASB116.R16', 'info',
          'Useful life outlier within class',
          `Asset ${r.asset_id} (${cls}): useful life ${r.useful_life_years}y deviates >50% from class median ${median}y.`,
          { rowId: r.asset_id, evidence: { useful_life_years: r.useful_life_years, classMedian: median } }));
      }
    }
  }

  // 4. Build summary
  const compliant = rows.length - new Set(
    findings.filter(f => f.severity === 'error' || f.severity === 'warning')
      .map(f => f.rowId).filter(Boolean)
  ).size;

  return {
    summary: {
      standard: 'AASB_116',
      rulepackVersion: RULEPACK_VERSION,
      rowsTested: rows.length,
      compliant,
      byseverity: {
        info: findings.filter(f => f.severity === 'info').length,
        warning: findings.filter(f => f.severity === 'warning').length,
        error: findings.filter(f => f.severity === 'error').length,
      },
      totalCarryingValue: totalCarrying,
      aggregateDelta: +aggregateDelta.toFixed(2),
      runAt: new Date().toISOString(),
    },
    findings,
    disclaimer: DISCLAIMER,
  };
}

function fmt(n: number): string {
  return n.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function acceptedHeadersFor(canonical: string): string[] {
  return Object.entries(HEADER_MAP).filter(([, c]) => c === canonical).map(([k]) => k);
}

const CLASS_LIFE_BANDS: Record<string, [number, number]> = {
  'buildings': [20, 60],
  'building': [20, 60],
  'plant & equipment': [3, 20],
  'plant and equipment': [3, 20],
  'plant': [3, 20],
  'motor vehicles': [4, 10],
  'vehicles': [4, 10],
  'it equipment': [2, 5],
  'ict': [2, 5],
  'furniture & fittings': [5, 15],
  'furniture and fittings': [5, 15],
};

export function detectAASB116(headerRow: string[]): { confidence: number; matched: string[]; missing: string[] } {
  const canonicalHits = new Set<string>();
  for (const raw of headerRow) {
    const c = normaliseHeader(String(raw ?? ''));
    if (c) canonicalHits.add(c);
  }
  const matched = REQUIRED.filter(r => canonicalHits.has(r));
  const missing = REQUIRED.filter(r => !canonicalHits.has(r));
  const confidence = matched.length / REQUIRED.length;
  return { confidence, matched, missing };
}
